import { AlertCircle, Check, Copy, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import { Skeleton } from "../ui/skeleton.js";

export interface TenantSettingsProps {
  organizationId: string;
  /** Allow editing name/slug (org owner/admin or superadmin). */
  canManage?: boolean;
  canDelete?: boolean;
  onDeleted?: () => void;
  /** Called with the new uuid after a successful id change. */
  onIdChanged?: (newId: string) => void;
}

export const TenantSettings = ({
  organizationId,
  canManage = false,
  canDelete = false,
  onDeleted,
  onIdChanged,
}: TenantSettingsProps) => {
  const client = useUmClient();
  const { data, loading, error, refresh } = useCall("getFullTenant", {
    organizationId,
  });
  // Resolve projectId → product name (superadmin sees all projects;
  // for others this may miss, in which case the raw id is shown).
  const projects = useCall("listProjects");
  const productName = data?.projectId
    ? (projects.data?.projects ?? []).find((p) => p.id === data.projectId)?.name
    : undefined;
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const copyId = async () => {
    await navigator.clipboard.writeText(organizationId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    const res = await client.adminDeleteTenant({ id: organizationId });
    setDeleting(false);
    if (res.ok) {
      onDeleted?.();
    } else {
      setDeleteError(errorMessage(res.error, "Failed to delete tenant."));
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            {canManage ? "Update tenant information" : "Tenant information"}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {loading ? (
            <>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-64" />
            </>
          ) : data ? (
            <>
              {canManage ? (
                <GeneralForm
                  organizationId={organizationId}
                  name={data.name}
                  slug={data.slug}
                  onSaved={refresh}
                />
              ) : (
                <>
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs">Name</Label>
                    <p className="text-sm font-medium">{data.name}</p>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs">Slug</Label>
                    <p className="font-mono text-sm">{data.slug}</p>
                  </div>
                </>
              )}
              {data.projectId ? (
                <div className="grid gap-1">
                  <Label className="text-muted-foreground text-xs">Product</Label>
                  <p className="text-sm font-medium">
                    {productName ?? <code className="font-mono text-xs">{data.projectId}</code>}
                  </p>
                </div>
              ) : null}
              <div className="grid gap-1">
                <Label className="text-muted-foreground text-xs">UUID</Label>
                <div className="flex items-center gap-2">
                  <code className="rounded bg-muted px-2 py-1 font-mono text-xs">
                    {organizationId}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={copyId}
                    aria-label="Copy UUID"
                  >
                    {copied ? (
                      <Check className="size-3.5 text-green-600" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      {canDelete && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger zone</CardTitle>
            <CardDescription>
              Deleting a tenant removes all its members, roles and data. This
              action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <ChangeIdForm
              organizationId={organizationId}
              onChanged={onIdChanged}
            />

            {deleteError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle />
                <AlertDescription>{deleteError}</AlertDescription>
              </Alert>
            )}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 /> Delete tenant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete tenant?</DialogTitle>
                  <DialogDescription>
                    This will permanently delete{" "}
                    <strong>{data?.name ?? "this tenant"}</strong> and all
                    associated data. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    loading={deleting}
                  >
                    Delete permanently
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const GeneralForm = ({
  organizationId,
  name: initialName,
  slug: initialSlug,
  onSaved,
}: {
  organizationId: string;
  name: string;
  slug: string;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState(initialSlug);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server data refreshed underneath us (e.g. another tab saved) —
  // resync untouched fields.
  useEffect(() => setName(initialName), [initialName]);
  useEffect(() => setSlug(initialSlug), [initialSlug]);

  const dirty = name.trim() !== initialName || slug.trim() !== initialSlug;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.updateTenant({
      organizationId,
      data: { name: name.trim(), slug: slug.trim() },
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't save tenant settings."));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="tenant-name">
          <Input
            id="tenant-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Slug" htmlFor="tenant-slug">
          <Input
            id="tenant-slug"
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="font-mono"
          />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" loading={saving} disabled={!dirty || !name.trim() || !slug.trim()}>
          {saved ? <Check /> : <Save />}
          {saved ? "Saved" : "Save changes"}
        </Button>
      </div>
    </form>
  );
};

// Superadmin-only uuid rewrite — members, invitations, roles and
// live sessions are repointed server-side in one transaction. Meant
// for aligning the tenant uuid with an external system's key.
const ChangeIdForm = ({
  organizationId,
  onChanged,
}: {
  organizationId: string;
  onChanged?: (newId: string) => void;
}) => {
  const client = useUmClient();
  const [newId, setNewId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    /^[A-Za-z0-9_-]{1,64}$/.test(newId.trim()) && newId.trim() !== organizationId;

  const submit = async () => {
    setBusy(true);
    setError(null);
    const res = await client.adminChangeTenantId({
      id: organizationId,
      newId: newId.trim(),
    });
    setBusy(false);
    if (res.ok) {
      onChanged?.(res.data.id);
    } else {
      setError(errorMessage(res.error, "Couldn't change the tenant UUID."));
    }
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor="tenant-new-id">Change UUID</Label>
      <p className="text-muted-foreground text-xs">
        Rewrites the tenant's uuid and repoints members, roles and live
        sessions. Use it to match an external system's key. Signed-in
        users of this tenant may see errors until their next token
        refresh.
      </p>
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex items-center gap-2">
        <Input
          id="tenant-new-id"
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          placeholder="new-uuid"
          className="max-w-sm font-mono"
        />
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="destructive" disabled={!valid || busy}>
              Change UUID
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change tenant UUID?</DialogTitle>
              <DialogDescription>
                The tenant id becomes{" "}
                <code className="font-mono text-xs">{newId.trim()}</code>. Every
                reference is repointed; external systems keyed on the old uuid
                must be updated in the same maintenance window.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <DialogClose asChild>
                <Button variant="destructive" onClick={() => void submit()} loading={busy}>
                  Change UUID
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
